import { useState, useEffect } from 'react';
import kit from './kit.module.css';
import styles from './ClarificationDemoVoice.module.css';

type State = 'idle' | 'playing' | 'done';

interface Line {
  role: 'user' | 'agent';
  text: string;
  delay: number;
}

const LINES: Line[] = [
  { role: 'user', text: 'Create a CV for Marcus.', delay: 0 },
  { role: 'agent', text: 'One thing before I start - should the tone be formal, warm or direct?', delay: 1000 },
  { role: 'user', text: 'Warm.', delay: 2000 },
  { role: 'agent', text: 'Got it. Generating a CV with a warm, approachable tone...', delay: 3000 },
  { role: 'agent', text: 'CV ready to review.', delay: 4000 },
];

export function ClarificationDemoVoice() {
  const [state, setState] = useState<State>('idle');
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    if (state !== 'playing') return;
    setVisibleCount(0);

    let doneTimer: ReturnType<typeof setTimeout> | undefined;
    const timers = LINES.map((line, i) =>
      setTimeout(() => {
        setVisibleCount(i + 1);
        if (i === LINES.length - 1) {
          doneTimer = setTimeout(() => setState('done'), 800);
        }
      }, line.delay)
    );

    return () => {
      timers.forEach(clearTimeout);
      if (doneTimer) clearTimeout(doneTimer);
    };
  }, [state]);

  return (
    <div className={kit.root}>
      <div className={`${kit.orb} ${state === 'playing' ? kit.orbActive : ''}`} aria-hidden="true" />

      {state === 'idle' ? (
        <div className={styles.center}>
          <p className={kit.feedback}>One clarifying question, then straight to the task.</p>
          <button type="button" className={`${kit.btn} ${kit.btnPrimary}`} onClick={() => setState('playing')}>
            Play conversation
          </button>
        </div>
      ) : (
        <>
          <div className={kit.transcript} aria-live="polite">
            {LINES.slice(0, visibleCount).map((line, i) => (
              <div
                key={i}
                className={`${kit.voiceLine} ${line.role === 'agent' ? kit.voiceAgent : ''} ${styles.fadeIn}`}
              >
                <span className={kit.voiceWho}>{line.role === 'agent' ? 'AGENT' : 'YOU'}</span>
                <p>{line.text}</p>
              </div>
            ))}
          </div>
          {state === 'playing' && <div className={kit.cue}>(listening)</div>}
          {state === 'done' && (
            <p className={`${kit.feedback} ${styles.centerText}`}>One question. One answer. Task complete.</p>
          )}
        </>
      )}
    </div>
  );
}
